import { useEffect, useMemo, useState } from 'react';

import { decodeJwt, getAccessToken } from '@/lib/api-client';
import { activeUnitForUser, writeActiveUnit } from '@/lib/crm-active-unit';

import { useMyUnits } from './useBusinessUnits';

/**
 * BU-v2 P3-3 unit switcher state. The caller's "create into" operating unit —
 * what new records without an explicit/parent unit are born into (resolving the
 * multi-unit ambiguity that would otherwise 400). Reach-checked server-side.
 *
 * The selection is kept in localStorage (see crm-active-unit) so the API client
 * can attach it as a header without a store↔api-client import cycle. The user id
 * is taken from the JWT `sub` — the SAME source the API client uses — so the
 * switcher's display and the header can never disagree.
 */
export function useSelectedUnit() {
  const { data: units = [], isLoading } = useMyUnits();
  const userId = decodeJwt(getAccessToken() ?? '')?.sub ?? null;
  const [tick, setTick] = useState(0);

  const activeUnits = useMemo(() => units.filter((u) => u.isActive), [units]);

  // Re-read the stored selection whenever the user picks (tick) or identity changes.
  const stored = useMemo(() => activeUnitForUser(userId), [userId, tick]);

  // Computed selection: a valid stored pick, else the default unit, else the first.
  const selectedUnitId = useMemo(() => {
    const validStored = activeUnits.some((u) => u.id === stored) ? stored : null;
    return validStored ?? activeUnits.find((u) => u.isDefault)?.id ?? activeUnits[0]?.id ?? null;
  }, [activeUnits, stored]);

  // Keep localStorage in step with the DISPLAYED selection, so the API client
  // (which reads localStorage) always sends exactly what the switcher shows —
  // otherwise the switcher could read "Alpha" while creates still 400.
  useEffect(() => {
    if (userId && selectedUnitId && stored !== selectedUnitId) {
      writeActiveUnit({ userId, unitId: selectedUnitId });
    }
  }, [userId, selectedUnitId, stored]);

  const selectUnit = (unitId: string) => {
    if (userId) writeActiveUnit({ userId, unitId });
    setTick((t) => t + 1);
  };

  return { units: activeUnits, selectedUnitId, selectUnit, isLoading };
}
